import React, { useState, useMemo } from 'react';
import {
    Box, Typography, Paper, IconButton, Tooltip, Divider, Chip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link } from 'react-router-dom';
import { normalizeCohortLogic } from './querybuilder/cohortLogic';
import { highlightSql } from '../utilities/sqlHighlight';

const cohortLogicMetrics = (cohortLogic) => {
    const logic = normalizeCohortLogic(cohortLogic);
    if (!logic) return [];

    const metrics = [{ label: 'All Patients', value: logic.root_count.toLocaleString() }];
    const visit = (children) => {
        children.forEach(child => {
            if (child.type === 'filter') {
                metrics.push({ label: child.label, value: child.count.toLocaleString() });
                return;
            }
            visit(child.children);
            metrics.push({
                label: child.label || child.operator + ' group',
                value: child.count.toLocaleString(),
            });
        });
    };
    visit(logic.children);
    metrics.push({ label: 'Final Cohort', value: logic.final_count.toLocaleString() });
    return metrics;
};

// ---------------------------------------------------------------------------
// Inline markdown renderer — handles **bold**, *italic*, `code`, [link](url)
// ---------------------------------------------------------------------------
const renderInline = (text, keyPrefix = '') => {
    if (!text) return null;

    const PATTERNS = [
        { re: /\*\*(.+?)\*\*/s, render: (m, k) => <strong key={k}>{renderInline(m[1], k + '_b')}</strong> },
        { re: /\*(.+?)\*/s, render: (m, k) => <em key={k}>{renderInline(m[1], k + '_i')}</em> },
        { re: /`(.+?)`/, render: (m, k) => (
            <code key={k} style={{ backgroundColor: 'var(--app-surface-sunken)', color: 'var(--app-text)', borderRadius: 3, padding: '1px 4px', fontFamily: 'monospace', fontSize: '0.8em' }}>
                {m[1]}
            </code>
        )},
        { re: /\[([^\]]+)\]\(([^)]+)\)/, render: (m, k) => {
            const href = m[2].startsWith('#LINK:') ? m[2].slice(6) : m[2];
            const isExternal = href.startsWith('http');
            return isExternal
                ? <a key={k} href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#04aeea', fontWeight: 500 }}>{m[1]}</a>
                : <Link key={k} to={href} style={{ color: '#04aeea', fontWeight: 500, textDecoration: 'none' }}>{m[1]}</Link>;
        }},
    ];

    const elements = [];
    let remaining = text;
    let idx = 0;

    while (remaining.length > 0) {
        let earliest = null;
        let earliestIndex = Infinity;
        let earliestPattern = null;

        for (const pattern of PATTERNS) {
            const match = remaining.match(pattern.re);
            if (match && match.index < earliestIndex) {
                earliest = match;
                earliestIndex = match.index;
                earliestPattern = pattern;
            }
        }

        if (!earliest) {
            elements.push(<span key={`${keyPrefix}_t${idx++}`}>{remaining}</span>);
            break;
        }

        if (earliestIndex > 0) {
            elements.push(<span key={`${keyPrefix}_t${idx++}`}>{remaining.slice(0, earliestIndex)}</span>);
        }

        elements.push(earliestPattern.render(earliest, `${keyPrefix}_e${idx++}`));
        remaining = remaining.slice(earliestIndex + earliest[0].length);
    }

    return elements;
};

// ---------------------------------------------------------------------------
// Block-level markdown parser
// ---------------------------------------------------------------------------
const parseBlocks = (text) => {
    const lines = text.split('\n');
    const blocks = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];
        const trimmed = line.trim();

        if (!trimmed) { i++; continue; }

        // Heading levels
        if (trimmed.startsWith('### ')) {
            blocks.push({ type: 'h3', text: trimmed.slice(4) });
            i++; continue;
        }
        if (trimmed.startsWith('## ')) {
            blocks.push({ type: 'h2', text: trimmed.slice(3) });
            i++; continue;
        }
        if (trimmed.startsWith('# ')) {
            blocks.push({ type: 'h1', text: trimmed.slice(2) });
            i++; continue;
        }

        // Bold-only line → treat as section heading
        if (/^\*\*[^*]+\*\*:?$/.test(trimmed)) {
            blocks.push({ type: 'boldHeading', text: trimmed.replace(/^\*\*/, '').replace(/\*\*:?$/, '') });
            i++; continue;
        }

        // Bullet list — collect consecutive items
        if (/^[-*] /.test(trimmed)) {
            const items = [];
            while (i < lines.length && /^[-*] /.test(lines[i].trim())) {
                items.push(lines[i].trim().slice(2));
                i++;
            }
            blocks.push({ type: 'bullets', items });
            continue;
        }

        // Numbered list — collect consecutive items
        if (/^\d+\. /.test(trimmed)) {
            const items = [];
            while (i < lines.length && /^\d+\. /.test(lines[i].trim())) {
                items.push(lines[i].trim().replace(/^\d+\. /, ''));
                i++;
            }
            blocks.push({ type: 'numbered', items });
            continue;
        }

        // Regular paragraph — collect until structural break
        const paraLines = [];
        while (i < lines.length) {
            const t = lines[i].trim();
            if (!t) break;
            if (/^#{1,3} /.test(t)) break;
            if (/^\*\*[^*]+\*\*:?$/.test(t)) break;
            if (/^[-*] /.test(t)) break;
            if (/^\d+\. /.test(t)) break;
            paraLines.push(t);
            i++;
        }
        if (paraLines.length > 0) {
            blocks.push({ type: 'paragraph', text: paraLines.join('\n') });
        }
    }

    return blocks;
};

// ---------------------------------------------------------------------------
// Block renderer
// ---------------------------------------------------------------------------
const renderBlock = (block, idx) => {
    switch (block.type) {
        case 'h1':
            return <Typography key={idx} variant="h6" fontWeight={700} sx={{ mt: 1.5, mb: 0.5 }}>{renderInline(block.text, `${idx}`)}</Typography>;
        case 'h2':
            return <Typography key={idx} variant="subtitle1" fontWeight={700} sx={{ mt: 1.5, mb: 0.5 }}>{renderInline(block.text, `${idx}`)}</Typography>;
        case 'h3':
            return <Typography key={idx} variant="subtitle2" fontWeight={600} sx={{ mt: 1, mb: 0.25 }}>{renderInline(block.text, `${idx}`)}</Typography>;
        case 'boldHeading':
            return (
                <Typography key={idx} variant="body2" fontWeight={700} sx={{ mt: 1.25, mb: 0.25, color: 'text.primary' }}>
                    {block.text}
                </Typography>
            );
        case 'bullets':
            return (
                <Box key={idx} component="ul" sx={{ m: 0, pl: 2.5, mb: 0.5 }}>
                    {block.items.map((item, i) => (
                        <Box key={i} component="li" sx={{ mb: 0.25 }}>
                            <Typography variant="body2" component="span">{renderInline(item, `${idx}_${i}`)}</Typography>
                        </Box>
                    ))}
                </Box>
            );
        case 'numbered':
            return (
                <Box key={idx} component="ol" sx={{ m: 0, pl: 2.5, mb: 0.5 }}>
                    {block.items.map((item, i) => (
                        <Box key={i} component="li" sx={{ mb: 0.25 }}>
                            <Typography variant="body2" component="span">{renderInline(item, `${idx}_${i}`)}</Typography>
                        </Box>
                    ))}
                </Box>
            );
        case 'paragraph':
        default:
            return (
                <Typography key={idx} variant="body2" sx={{ mb: 0.75, whiteSpace: 'pre-line', lineHeight: 1.6, color: 'text.primary' }}>
                    {renderInline(block.text, `${idx}`)}
                </Typography>
            );
    }
};

// ---------------------------------------------------------------------------
// Plain markdown message
// ---------------------------------------------------------------------------
const MarkdownMessage = ({ text }) => {
    if (!text?.trim()) return null;
    const blocks = parseBlocks(text);
    return <Box>{blocks.map((b, i) => renderBlock(b, i))}</Box>;
};

// ---------------------------------------------------------------------------
// Structured Cortex response (sql_statement, narrative, query_filters, etc.)
// ---------------------------------------------------------------------------
const StructuredCortexResponse = ({ data, beforeText, afterText, suppressFlow = false }) => {
    const [copied, setCopied] = useState(false);

    // The SQL block sits on a fixed dark editor surface in both modes, so it is
    // always highlighted against the dark palette.
    const highlightedSql = useMemo(
        () => highlightSql(data.sql_statement, 'dark'),
        [data.sql_statement]
    );

    // results_count is now an array of per-condition results, one per condition set.
    // Each item is an object or a "Label: value" string. The agent is inconsistent
    // about object key names between runs — condition/count, condition_set/patient_count,
    // and label/value have all been observed — so accept every spelling. A row whose
    // keys aren't recognised is dropped silently, which reads as results_count
    // "sometimes" not rendering even though it is present in the response.
    const legacyMetrics = (Array.isArray(data.results_count) ? data.results_count : [])
        .map(item => {
            if (item && typeof item === 'object') {
                const label = item.condition ?? item.condition_set ?? item.label;
                const value = item.count ?? item.patient_count ?? item.value;
                if (label == null || value == null) return null;
                return { label: String(label), value: Number(value).toLocaleString() };
            }
            const trimmed = String(item ?? '').trim();
            if (!trimmed) return null;
            const lastColonIdx = trimmed.lastIndexOf(':');
            if (lastColonIdx === -1) return null;
            return { label: trimmed.slice(0, lastColonIdx).trim(), value: trimmed.slice(lastColonIdx + 1).trim() };
        })
        .filter(Boolean);
    const compactMetrics = data.cohort_logic ? cohortLogicMetrics(data.cohort_logic) : [];
    const metrics = compactMetrics.length > 0 ? compactMetrics : legacyMetrics;

    const hasFilterSteps =
        (Array.isArray(data.query_filters) && data.query_filters.length > 0) ||
        compactMetrics.length > 0;

    const handleCopy = () => {
        navigator.clipboard.writeText(data.sql_statement || '');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ width: '100%', fontSize: '0.875rem' }}>
            {/* Text before the JSON block */}
            {beforeText?.trim() && (
                <Box sx={{ mb: 1 }}>
                    <MarkdownMessage text={beforeText} />
                </Box>
            )}

            {/* Results breakdown — one stacked card per step */}
            {metrics.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
                    {metrics.map((m, i) => (
                        <Paper key={i} variant="outlined" sx={{
                            textAlign: 'center',
                            py: 1.25,
                            px: 1,
                            borderColor: '#04aeea22',
                            bgcolor: 'action.hover',
                            borderRadius: 2,
                        }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                                {m.label}
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#04aeea', mt: 0.25 }}>
                                {m.value}
                            </Typography>
                        </Paper>
                    ))}
                </Box>
            )}

            {/* Narrative */}
            {data.narrative_description && (
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: '0.05em' }}>
                        EXPLANATION
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6, color: 'text.primary' }}>
                        {data.narrative_description}
                    </Typography>
                </Box>
            )}

            {/* SQL block */}
            {data.sql_statement && (
                <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: '0.05em' }}>
                            SQL
                        </Typography>
                        <Tooltip title={copied ? 'Copied!' : 'Copy SQL'}>
                            <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5 }}>
                                {copied
                                    ? <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                    : <ContentCopyIcon sx={{ fontSize: 14 }} />
                                }
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box sx={{
                        bgcolor: '#1e1e2e',
                        borderRadius: 1,
                        p: 1.25,
                        maxHeight: 140,
                        overflowY: 'auto',
                        overflowX: 'auto',
                    }}>
                        <Typography component="pre" sx={{
                            color: '#cdd6f4',
                            fontSize: '0.7rem',
                            fontFamily: '"Fira Code", "Cascadia Code", "Consolas", monospace',
                            m: 0,
                            whiteSpace: 'pre',
                            lineHeight: 1.6,
                        }}>
                            {highlightedSql}
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Filter steps chip — on QueryBuilder, funnel was sent to Query Logic tab */}
            {hasFilterSteps && suppressFlow && (
                <Box sx={{ mb: 1.5 }}>
                    <Chip
                        icon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                        label="Patient funnel sent to Query Logic tab"
                        size="small"
                        sx={{
                            bgcolor: 'action.hover',
                            color: '#04aeea',
                            border: '1px solid #04aeea55',
                            fontWeight: 600,
                            fontSize: '0.72rem',
                        }}
                    />
                </Box>
            )}

            {/* Filter steps chip — outside QueryBuilder, offer navigation */}
            {hasFilterSteps && !suppressFlow && (
                <Box sx={{ mb: 1.5 }}>
                    <Link to="/queryBuilder" style={{ textDecoration: 'none' }}>
                        <Chip
                            icon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                            label="Open Query Builder to visualize this query"
                            size="small"
                            sx={{
                                bgcolor: 'action.hover',
                                color: '#04aeea',
                                border: '1px solid #04aeea55',
                                fontWeight: 600,
                                fontSize: '0.72rem',
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'action.selected' },
                            }}
                        />
                    </Link>
                </Box>
            )}

            {/* Text after the JSON block */}
            {afterText?.trim() && (
                <Box sx={{ mt: 0.5 }}>
                    <Divider sx={{ mb: 1 }} />
                    <MarkdownMessage text={afterText} />
                </Box>
            )}
        </Box>
    );
};

// ---------------------------------------------------------------------------
// Main exported renderer — detects response type and routes to the right view
// ---------------------------------------------------------------------------
const ChatMessageRenderer = ({ text, suppressFlow = false }) => {
    // Detect structured JSON response: either fenced ```json block, or a raw
    // JSON object embedded in the text (newer Cortex agent replies omit the fence)
    const fencedMatch = text.match(/```json\s*\n([\s\S]*?)\n?```/);
    const rawMatch = !fencedMatch && text.match(/(\{[\s\S]*"(?:cohort_logic|sql_statement|results_count|query_filters)"[\s\S]*\})/);
    const jsonBlockMatch = fencedMatch || rawMatch;
    if (jsonBlockMatch) {
        let data = null;
        try {
            data = JSON.parse(jsonBlockMatch[1]);
        } catch (e) {
            console.warn('Failed to parse structured Cortex response:', e);
        }

        if (data && (data.cohort_logic || data.sql_statement || data.results_count || data.query_filters)) {
            const blockStart = text.indexOf(jsonBlockMatch[0]);
            const blockEnd = blockStart + jsonBlockMatch[0].length;
            const beforeText = text.slice(0, blockStart).trim();
            const afterText = text.slice(blockEnd).trim();
            return <StructuredCortexResponse data={data} beforeText={beforeText} afterText={afterText} suppressFlow={suppressFlow} />;
        }
    }

    // Fallback: plain markdown
    return <MarkdownMessage text={text} />;
};

export default ChatMessageRenderer;
